// @steered AudibleSecurityContext 1.2 2026-09-21
/**
 * Submission status for a G2S2 change made through QuickLab.
 *
 *   ready      — records exist in a stage version; nothing sent yet
 *   in_review  — submitted to Slack; ORCA reviews and deploys (assumed state:
 *                there is no callback from the CR/pipeline, and we don't fake one)
 *   live       — every record the session wrote reads back from the PROD label
 *                with the same payload. This is the only verified state.
 *
 * "Live" is decided by data, never by time or by the pipeline: the prod label
 * is what customers get, so reading it back is the truth. The comparison runs
 * server-side (StrandsAgents `liveStatus`), so the browser never talks to
 * G2S2HelperFunction; this module owns the client-side state around it.
 */
import type { SubmissionLiveStatus } from '../services/LambdaService';

export type SubmissionState = 'ready' | 'in_review' | 'live';

/** Persisted per session; timestamps are epoch ms. */
export interface SubmissionStatus {
    submittedAt: number | null;
    liveAt: number | null;
    lastCheckedAt: number | null;
}

export const EMPTY_SUBMISSION_STATUS: SubmissionStatus = { submittedAt: null, liveAt: null, lastCheckedAt: null };

/** G2S2 changes take roughly this long to propagate even after deployment; don't read prod before then. */
export const PROPAGATION_GRACE_MS = 15 * 60 * 1000;

export const deriveSubmissionState = (status: SubmissionStatus): SubmissionState => {
    if (status.liveAt) return 'live';
    if (status.submittedAt) return 'in_review';
    return 'ready';
};

/** True once enough time has passed since submit for a prod read to be meaningful. */
export const isPastPropagationGrace = (status: SubmissionStatus, now: number = Date.now()): boolean =>
    status.submittedAt !== null && now - status.submittedAt >= PROPAGATION_GRACE_MS;

/** What the card shows after a check; the server-side result plus nothing else. */
export type LiveCheckResult = SubmissionLiveStatus;

/** Apply a check result to the persisted status. Only a verified `live` ever sets liveAt. */
export const applyLiveCheck = (status: SubmissionStatus, result: LiveCheckResult, now: number = Date.now()): SubmissionStatus => ({
    ...status,
    lastCheckedAt: now,
    liveAt: result.live ? (status.liveAt ?? now) : status.liveAt,
});
